(() => {
  if (customElements.get('product-form')) {
    return;
  }

  class ProductForm extends HTMLElement {
    constructor() {
      super();
      this.form = this.querySelector('form');
      this.submitButton = this.form.querySelector('[name="add"]');
      this.cartDrawer = document.querySelector('cart-drawer');
      this.productBar = document.querySelector(
        `#ProductBar-${this.dataset.sectionId}`
      );

      this.form.addEventListener(
        'submit',
        this.onSubmitHandler.bind(this)
      );
    }

    onSubmitHandler(event) {
      event.preventDefault();

      const purchaseOptions = document.querySelector('purchase-options');
      if (
        !!purchaseOptions &&
        !purchaseOptions.isOneTimePurchase() &&
        !this.hasAttribute('data-has-selling-plan')
      ) {
        purchaseOptions.showError();
        return;
      }

      this.submitButton.classList.add('disabled');

      const formData = new FormData(this.form);
      const items = this.buildItems(formData);

      // Made-to-order products go to the cart page rather than the drawer, so
      // the customer reviews the extras and lead time before checking out.
      const redirectToCart = this.hasAttribute('data-redirect-to-cart');

      // Multipart, not JSON: several storefront apps (Swym, the upsell app,
      // the events listener) wrap window.fetch and expect a FormData body on
      // this route. /cart/add.js accepts multiple lines via indexed field
      // names, so the extras ship as their own lines without changing the
      // request shape those wrappers assume.
      const config = fetchConfig('javascript');
      config.headers['X-Requested-With'] = 'XMLHttpRequest';
      delete config.headers['Content-Type'];

      const body = new FormData();

      items.forEach((item, index) => {
        body.append(`items[${index}][id]`, item.id);
        body.append(`items[${index}][quantity]`, item.quantity);

        if (item.selling_plan) {
          body.append(`items[${index}][selling_plan]`, item.selling_plan);
        }

        Object.entries(item.properties || {}).forEach(([key, value]) => {
          body.append(`items[${index}][properties][${key}]`, value);
        });
      });

      // No point rendering drawer sections we are about to navigate away from.
      if (this.cartDrawer && !redirectToCart) {
        body.append(
          'sections',
          this.cartDrawer
            .getSectionsToRender()
            .map(section => section.section)
        );
        body.append('sections_url', window.location.pathname);
      }

      config.body = body;

      fetch(`${routes.cart_add_url}`, config)
        .then(response => response.text())
        .then(text => {
          // A 400 from /cart/add can come back with an empty body, so parse
          // defensively rather than letting response.json() throw past the
          // error handler.
          let response;

          try {
            response = JSON.parse(text);
          } catch (error) {
            console.error('cart/add returned non-JSON:', text);
            this.handleErrorMessage(
              window.variantStrings?.error ||
                'Something went wrong. Please try again.'
            );
            return;
          }

          if (response.status) {
            this.handleErrorMessage(response.description);
            return;
          }

          this.handleErrorMessage();

                  if (redirectToCart) {
            window.location.href = routes.cart_url || `${routes.root_url || ''}/cart`.replace('//', '/');
            return;
          }

          this.cartDrawer?.renderContents(response);
        })
        .catch(error => {
          console.error(error);
          this.handleErrorMessage(
            window.variantStrings?.error ||
              'Something went wrong. Please try again.'
          );
        })
        .finally(() => {
          this.submitButton.classList.remove('disabled');
        });
    }

    /**
     * The product line plus one line per checked extra.
     *
     * The extras also write line item properties onto the product line, which
     * is what fulfilment reads. The separate lines are what the customer pays,
     * since Shopify does not price line item properties. A shared _bundle_id
     * ties them together for the cart; properties prefixed with an underscore
     * are hidden from the customer.
     */
    buildItems(formData) {
      // product-selector maintains its own hidden id input, associated to the
      // form by the form attribute rather than nesting. Read the last one so a
      // stale default-variant input in the buy_buttons block cannot win.
      const idValues = formData.getAll('id');
      const variantId = Number(idValues[idValues.length - 1]);

      const item = {
        id: variantId,
        quantity: Number(formData.get('quantity')) || 1,
        properties: {}
      };

      const sellingPlan = formData.get('selling_plan');
      if (sellingPlan) {
        item.selling_plan = Number(sellingPlan);
      }

      formData.forEach((value, key) => {
        const match = key.match(/^properties\[(.+)\]$/);
        if (match && value !== '') {
          item.properties[match[1]] = value;
        }
      });

      // form.elements resolves inputs associated by the form attribute, the
      // same way FormData does. Note this.form.id is NOT the id attribute
      // here: the hidden <input name="id"> shadows it, so never build
      // selectors from it.
      const checkedExtras = Array.from(this.form.elements).filter(
        element => element.matches?.('[data-extra]') && element.checked
      );

      if (checkedExtras.length === 0) {
        return [item];
      }

      const bundleId = `${Date.now()}-${Math.random()
        .toString(36)
        .slice(2, 8)}`;

      item.properties._bundle_id = bundleId;

      const extraItems = checkedExtras.map(input => ({
        id: Number(input.dataset.extraVariantId),
        quantity: item.quantity,
        properties: {
          _bundle_id: bundleId,
          _bundle_parent: item.id
        }
      }));

      return [item, ...extraItems];
    }

    handleErrorMessage(errorMessage = false) {
      const errorWrapper = this.querySelector('[data-error-wrapper]');
      if (!errorWrapper) return;

      errorWrapper.classList.toggle('hidden', !errorMessage);
      errorWrapper.textContent = errorMessage || '';
    }

    toggleAddButton(disable, text) {
      [
        this.submitButton,
        this.productBar?.querySelector('[name="add"]')
      ].forEach(submitButton => {
        if (disable) {
          submitButton?.setAttribute('disabled', 'disabled');
          if (text && submitButton) submitButton.textContent = text;
        } else if (submitButton) {
          submitButton.removeAttribute('disabled');
          submitButton.textContent = this.hasAttribute('data-has-selling-plan')
            ? window.variantStrings.addSubscriptionToCart
            : window.variantStrings.addToCart;
        }
      });
    }
  }

  customElements.define('product-form', ProductForm);
})();